import { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import gsap from 'gsap';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

const navLinks = [
  { translationKey: 'home', href: '/' },
  {
    translationKey: 'seminary',
    href: '#',
    subLinks: [
      { translationKey: 'history', href: '/historia' },
      { translationKey: 'news', href: '/noticias' },
    ],
  },
  { translationKey: 'contact', href: '/contactenos' },
  { translationKey: 'donations', href: '/donaciones' },
];

export function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLUListElement | null }>({});

  // Mobile menu animation
  useEffect(() => {
    if (isOpen) {
      gsap.to(mobileMenuRef.current, {
        height: 'auto',
        opacity: 1,
        duration: 0.4,
        ease: 'power3.out',
      });
    } else {
      gsap.to(mobileMenuRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      });
    }
  }, [isOpen]);

  // Desktop dropdown animation
  useEffect(() => {
    Object.entries(dropdownRefs.current).forEach(([name, ref]) => {
      if (!ref) return;
      if (activeDropdown === name) {
        gsap.to(ref, {
          autoAlpha: 1,
          y: 0,
          duration: 0.3,
          ease: 'power2.out',
          display: 'block'
        });
      } else {
        gsap.to(ref, {
          autoAlpha: 0,
          y: -10,
          duration: 0.2,
          ease: 'power2.in',
          display: 'none'
        });
      }
    });
  }, [activeDropdown]);

  return (
    <header className="fixed top-0 w-full z-50 bg-zinc-950/80 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="/logoSRMWhite.svg" 
                alt="Seminario Redemptoris Mater de Medellín" 
                className="h-16 w-auto"
                width="64"
                height="64"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div
                key={link.translationKey}
                className="relative"
                onMouseEnter={() => link.subLinks && setActiveDropdown(link.translationKey)}
                onMouseLeave={() => link.subLinks && setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className="font-neocat text-sm font-medium tracking-widest hover:text-zinc-400 transition-colors duration-300 flex items-center gap-1 py-2 uppercase"
                >
                  {t(`navbar.${link.translationKey}`)}
                  {link.subLinks && <ChevronDown size={14} className={cn("transition-transform duration-300", activeDropdown === link.translationKey ? "rotate-180" : "")} />}
                </Link>

                {/* Dropdown */}
                {link.subLinks && (
                  <div className="absolute left-0 top-full pt-2">
                    <ul
                      ref={(el) => {
                        if (el) dropdownRefs.current[link.translationKey] = el;
                      }}
                      className="w-48 bg-zinc-900 border border-white/10 rounded-sm shadow-xl overflow-hidden hidden opacity-0 -translate-y-2"
                    >
                      {link.subLinks.map((subLink) => (
                        <li key={subLink.translationKey}>
                          <Link
                            to={subLink.href}
                            onClick={() => setActiveDropdown(null)}
                            className="block px-4 py-3 text-xs font-neocat tracking-wider hover:bg-zinc-800 hover:text-white transition-colors uppercase"
                          >
                            {t(`navbar.${subLink.translationKey}`)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-zinc-50 hover:text-zinc-400 transition-colors p-2"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        ref={mobileMenuRef}
        className="md:hidden overflow-hidden h-0 opacity-0 bg-zinc-950 border-b border-white/10"
      >
        <div className="px-4 pt-2 pb-6 space-y-1">
          {navLinks.map((link) => (
            <div key={link.translationKey}>
              <Link
                to={link.href}
                onClick={() => !link.subLinks && setIsOpen(false)}
                className="block px-3 py-4 text-base font-neocat tracking-widest hover:bg-zinc-900 hover:text-white transition-colors uppercase"
              >
                {t(`navbar.${link.translationKey}`)}
              </Link>
              {link.subLinks && (
                <div className="pl-6 bg-zinc-900/30">
                  {link.subLinks.map((subLink) => (
                    <Link
                      key={subLink.translationKey}
                      to={subLink.href}
                      onClick={() => setIsOpen(false)}
                      className="block px-3 py-3 text-sm font-neocat tracking-wider hover:text-white transition-colors uppercase"
                    >
                      {t(`navbar.${subLink.translationKey}`)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}
